import { EventType } from '@azure/msal-browser';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

import { createApiClient } from '../api/client.js';
import { getDatalakeAccount, getDatalakePca } from '../auth/datalakeAccount.js';
import { datalakeApiRequest } from '../auth/msalConfig.js';
import { config } from '../config.js';
import { AccessControlService } from '../services/accessControlService.js';

interface DataHubPermissionState {
  isLoading: boolean;
  isAdmin: boolean;
  refreshPermissions: () => void;
}

const DataHubPermissionContext = createContext<DataHubPermissionState | null>(
  null,
);

export function DataHubPermissionProvider({ children }: PropsWithChildren) {
  const instance = getDatalakePca();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [checkCount, setCheckCount] = useState(0);

  const refreshPermissions = useCallback(() => {
    setCheckCount((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkRole() {
      try {
        await instance.initialize();
        const account = getDatalakeAccount(instance);
        if (!account) {
          if (!cancelled) setIsAdmin(false);
          return;
        }

        const { accessToken } = await instance.acquireTokenSilent({
          ...datalakeApiRequest,
          account,
        });

        const client = createApiClient(
          () => Promise.resolve(accessToken),
          config.datalakeApiBaseUrl,
        );
        const svc = new AccessControlService(client);
        const role = await svc.getMyTenantRole();

        if (!cancelled) setIsAdmin(role.isAdmin);
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    setIsLoading(true);
    void checkRole();

    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        void checkRole();
      }
    });

    return () => {
      cancelled = true;
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance, checkCount]);

  return (
    <DataHubPermissionContext.Provider
      value={{ isLoading, isAdmin, refreshPermissions }}
    >
      {children}
    </DataHubPermissionContext.Provider>
  );
}

export function useDataHubPermission(): DataHubPermissionState {
  const ctx = useContext(DataHubPermissionContext);
  if (!ctx) {
    throw new Error(
      'useDataHubPermission must be used within <DataHubPermissionProvider>',
    );
  }
  return ctx;
}
